import { useContext, useEffect, useMemo } from 'react';
import { ParsedUrlQuery } from 'querystring';
import { useRouter } from 'next/router';

import { isClient } from '../lib/utils';
import BaseTemplate from './Templates/BaseTemplate';
import LiteSiteTemplate from './Templates/LiteSiteTemplate';
import RsvpTemplate from './Templates/RsvpTemplate';
import SinglePageTemplate from './Templates/SinglePageTemplate';
import StandaloneRegistryTemplate from './Templates/StandaloneRegistryTemplate';
import { useRsvpIsVisible } from '../lib/useRsvpIsVisible';
import { useIsLiteSite } from '../lib/useIsLiteSite';
import { WWContext, WWContextValue } from './WWContext';
import {
  isWwsWeddingWebsiteV2,
  WeddingWebsiteQuery,
  Wws_WeddingWebsiteV2,
} from '../types/graphql';
import useTracking from '../lib/useTracking';
import { useCurrentRoute } from '../lib/useCurrentRoute';
import { InlineEditProvider } from '../context/InlineEditContext';

export const buildViewSinglePage = (
  data: WeddingWebsiteQuery | null,
  query: ParsedUrlQuery
) => {
  const isV2 = isWwsWeddingWebsiteV2(data?.weddingWebsiteResult);

  // we will temporarily use this to determine if we should show the single page view
  // in the future this will be controlled by a field on the website data.
  const doesViewSinglePageQueryParamHaveAValue = !!query['viewSinglePage'];
  const isWWSInSinglePageView =
    isV2 &&
    (data?.weddingWebsiteResult as Wws_WeddingWebsiteV2)?.layout ===
      'single-page';

  // This allows us to override the layout value with the query param for testing purposes
  const viewSinglePage =
    isWWSInSinglePageView && !doesViewSinglePageQueryParamHaveAValue
      ? isWWSInSinglePageView
      : query['viewSinglePage'] === 'true';

  return viewSinglePage;
};

const TemplateWrapper = () => {
  const { query, replace } = useRouter();
  const isRsvpVisible = useRsvpIsVisible();
  const isLiteSite = useIsLiteSite();
  const { data, setSinglePageLayout } = useContext<WWContextValue>(WWContext);

  const viewSinglePage = buildViewSinglePage(data, query);
  const isInEditMode = query['isInEditMode'] === 'true';

  useEffect(() => {
    setSinglePageLayout(viewSinglePage);
  }, [viewSinglePage, setSinglePageLayout]);

  const currentRoute = useCurrentRoute();

  const track = useTracking('visit', currentRoute);

  useEffect((): void => {
    track('guest visit');
  }, [track]);

  const themeOverride = useMemo<string>((): string => {
    if (typeof query['themeOverride'] === 'string') {
      return query['themeOverride'];
    }

    return '';
  }, [query]);

  let Template;

  if (currentRoute === 'rsvp') {
    const preview = query['preview'] === 'true';
    if (!isRsvpVisible && isClient() && !preview) {
      replace('/404');

      return null;
    }

    Template = RsvpTemplate;
  } else if (isLiteSite) {
    Template = LiteSiteTemplate;
  } else if (viewSinglePage) {
    if (currentRoute === 'registry') {
      Template = StandaloneRegistryTemplate;
    } else {
      Template = SinglePageTemplate;
    }
  } else {
    Template = BaseTemplate;
  }

  return (
    <InlineEditProvider isInEditMode={isInEditMode}>
      <Template themeOverride={themeOverride} />
    </InlineEditProvider>
  );
};

export default TemplateWrapper;
